import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { ToastInterface, useToast } from "vue-toastification";

interface Props {
  immediate?: boolean;
  params?: Params;
}

interface Params {
  id: string | number;
}

interface UpdateParams {
  id: string | number;
  contactTypeIds: number[];
  name: string;
  jobTitle: string;
  contactableType?: string;
}

const getContacts = async (contact: any) => {
  const { data } = await axios.get(`/api/internal/contacts/${contact.id}`);
  return data;
};

const updateContact = async (params: UpdateParams, toast: ToastInterface) => {
  try {
    const { data } = await axios.put(`/api/internal/contacts/${params.id}`, {
      contactTypeIds: params.contactTypeIds,
      name: params.name,
      jobTitle: params.jobTitle,
      contactableType: params.contactableType,
    });
    return data;
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const deleteContact = async (params: Params, toast: ToastInterface) => {
  try {
    await axios.delete(`/api/internal/contacts/${params.id}`);
    toast.success("Se elimino correctamente el contacto");
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const restoreContact = async (params: Params, toast: ToastInterface) => {
  try {
    await axios.post(`/api/internal/contacts/${params.id}/restore`);
    toast.success("Se restauro correctamente el contacto");
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const useContacts = (props: Props = {}) => {
  const toast = useToast();

  const { data, isLoading, isError, refetch } = useQuery(
    ["contacts", props.params],
    () => getContacts(props.params!),
    { enabled: props.immediate },
  );

  const updateContactMutation = useMutation((contact: UpdateParams) =>
    updateContact(contact, toast),
  );

  const deleteContactMutation = useMutation((contact: Params) =>
    deleteContact(contact, toast),
  );

  const restoreContactMutation = useMutation((contact: Params) =>
    restoreContact(contact, toast),
  );

  return {
    contacts: data,
    isLoading,
    isError,
    refetch,

    updateContact: updateContactMutation.mutate,
    updateContactAsync: updateContactMutation.mutateAsync,
    updateContactLoading: updateContactMutation.isLoading,

    deleteContact: deleteContactMutation.mutate,
    deleteContactAsync: deleteContactMutation.mutateAsync,
    deleteContactLoading: deleteContactMutation.isLoading,

    restoreContact: restoreContactMutation.mutate,
    restoreContactAsync: restoreContactMutation.mutateAsync,
    restoreContactLoading: restoreContactMutation.isLoading,
  };
};

export default useContacts;
